import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import HeroImageSection from "../components/HeroImageSection";
import {
  CalendarDays,
  Clock3,
  ArrowLeft,
  User2,
} from "lucide-react";
import solarservices from "../assets/solarservice.jpeg";
import { fetchBlogBySlugFromWp } from "../data/wpApi";
import slugRedirects from "../data/slugRedirects";

// PROTOTYPE: fetches from a local headless WordPress instance instead of
// the static src/data/BlogsData.jsx array. See src/data/wpApi.js.
const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchBlogBySlugFromWp(slug, solarservices)
      .then(setBlog)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <section className="min-h-screen bg-[#f8faf8] flex items-center justify-center px-4">
        <p className="text-[#6b7280]">Loading…</p>
      </section>
    );
  }

  if (!blog && slugRedirects[slug]) {
    return <Navigate to={`/blogs/${slugRedirects[slug]}`} replace />;
  }

  if (!blog) {
    return (
      <section className="min-h-screen bg-[#f8faf8] flex items-center justify-center px-4">
        <div className="bg-white max-w-[620px] w-full rounded-3xl border border-[#e5e7eb] shadow-sm p-8 text-center">
          <h2 className="text-[30px] font-bold text-[#111827] mb-3">
            Blog Not Found
          </h2>

          <p className="text-[#6b7280] text-[16px] leading-7 mb-7">
            The article you’re looking for may have been moved or no longer
            exists.
          </p>

          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 rounded-full bg-[#117816] px-6 py-3 text-white font-semibold hover:opacity-90 transition"
          >
            <ArrowLeft size={18} />
            Back to Blogs
          </Link>
        </div>
      </section>
    );
  }

  const canonicalUrl = `https://www.savorka.in/blogs/${blog.slug}`;

  return (
    <div className="bg-[#f7faf7] min-h-screen">
      <Helmet>
        <title>{`${blog.title} | Savorka Solar Blog`}</title>
        <meta name="description" content={blog.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.metaDescription} />
        <meta property="og:image" content={blog.image} />
        <meta property="og:url" content={canonicalUrl} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.metaDescription} />
        <meta name="twitter:image" content={blog.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: blog.title,
            description: blog.metaDescription,
            image: blog.image,
            datePublished: blog.dateIso,
            author: {
              "@type": "Person",
              name: blog.author,
            },
            publisher: {
              "@type": "Organization",
              name: "Savorka",
              logo: {
                "@type": "ImageObject",
                url: "https://www.savorka.in/path-to-logo.png",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": canonicalUrl,
            },
          })}
        </script>
      </Helmet>

      {/* HERO IMAGE COMPONENT */}
      <div className="w-full bg-white">
  <HeroImageSection
    heroImage={blog.image}
    imageClassName="scale-[2.5] object-cover"
    altText={blog.title}
  />
</div>

      {/* HERO CONTENT BELOW IMAGE */}
      <section className="relative border-b border-[#eef2ee] bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="max-w-[920px] mx-auto text-center">
            <span className="inline-flex rounded-full bg-[#e8f7ea] px-4 py-2 text-[12px] tracking-[0.14em] uppercase font-bold text-[#117816] mb-5">
              {blog.category}
            </span>

            <h1 className="text-[28px] sm:text-[38px] lg:text-[52px] leading-[1.1] font-bold text-[#111827] mb-6">
              {blog.title}
            </h1>

<p
  className="text-[#4b5563] text-[16px] sm:text-[18px] leading-8 mb-8"
  dangerouslySetInnerHTML={{ __html: blog.shortDescription }}
/>
            {/* Meta */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-[#6b7280] text-[14px]">
              <span className="inline-flex items-center gap-2">
                <User2 size={16} />
                {blog.author}
              </span>

              <span className="inline-flex items-center gap-2">
                <CalendarDays size={16} />
                {blog.date}
              </span>

              <span className="inline-flex items-center gap-2">
                <Clock3 size={16} />
                {blog.readTime}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[920px] mx-auto">
          <article className="bg-white rounded-[28px] border border-[#edf1ed] shadow-sm p-6 sm:p-10 md:p-14">
            <div
              className="wp-content"
              dangerouslySetInnerHTML={{ __html: blog.contentHtml }}
            />
          </article>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;
